import React, { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { LineChart as LineChartIcon, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { MappingIncompleteBanner } from '../../components/broker-activity/MappingIncompleteBanner';
import { usePipelineReport } from '../../hooks/usePipelineReport';
import { formatCompactCurrency } from '../../utils/formatters';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

// Mockup mycrm-integration §Deal Pipeline: funnel chart + stale deals.

const fmtDollars = (cents: number): string => formatCompactCurrency(cents);

function fmtStatus(s: string): string {
  return s
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Palette — generate deterministic color per status by rotating through a
// fixed palette. Keeps chart colors stable across renders without an
// explicit status→color map that would lag new statuses.
const FUNNEL_COLORS = [
  '#3b82f6',
  '#22c55e',
  '#f59e0b',
  '#8b5cf6',
  '#ef4444',
  '#06b6d4',
  '#ec4899',
  '#14b8a6',
  '#a855f7',
  '#eab308',
];

const PipelinePage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;
  // Marketing can't reach CRM setup/connection screens, so reword empty-state
  // copy and drop those links to admin-only destinations (marketing RBAC §6).
  const isMarketing = userData?.role === 'marketing';
  // Recency window over deal CREATION date (crm_created_at), NOT snapshot_date.
  // Bounds the funnel to deals created in the last N days so stale/dead
  // NewLeads accumulated over years don't inflate the "in pipeline" count.
  const [lookbackDays, setLookbackDays] = useState<number>(30);

  const { data, loading, error, refresh } = usePipelineReport(
    companyId,
    lookbackDays
  );

  const funnelMax = useMemo(() => {
    if (!data || data.funnel.length === 0) return 0;
    return Math.max(...data.funnel.map((f) => f.dealCount));
  }, [data]);

  const hasData = (data?.totalDealCount ?? 0) > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={LineChartIcon}
        title="Deal Pipeline"
        subtitle="Live pipeline from MyCRM — funnel by status + stale-deal detection (30+ day no-change threshold)"
        onRefresh={refresh}
        isRefreshing={loading}
      />

      <MappingIncompleteBanner />

      {/* Creation-window selector — narrows the funnel to deals CREATED in
          the last N days (crm_created_at), so dead/stale NewLeads don't
          inflate the counts. */}
      <div className="flex items-center gap-3 text-sm">
        <label className="text-gray-400">Deals created in last</label>
        <select
          value={lookbackDays}
          onChange={(e) => setLookbackDays(Number(e.target.value))}
          className="px-3 py-1.5 rounded-md bg-gray-800 border border-gray-700 text-white text-sm"
        >
          <option value={30}>30 days</option>
          <option value={60}>60 days</option>
          <option value={90}>90 days</option>
          <option value={180}>180 days</option>
        </select>
      </div>

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-blue-500 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            Deals in pipeline
          </div>
          <div className="mt-1 text-3xl font-bold text-blue-400">
            {(data?.totalDealCount ?? 0).toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            in-flight deals created in the window — excludes settled / withdrawn / repaid / archived / cancelled / declined
          </div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-emerald-500 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            Pipeline loan volume
          </div>
          <div className="mt-1 text-3xl font-bold text-emerald-400">
            {fmtDollars(data?.totalLoanAmountCents ?? 0)}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            sum of current loan amounts
          </div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-amber-500 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            Stale deals (30+ days)
          </div>
          <div className="mt-1 text-3xl font-bold text-amber-400">
            {(data?.staleDeals.length ?? 0).toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            no status change — needs follow-up
          </div>
        </div>
      </div>

      {!loading && !hasData && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-8 text-center">
          <LineChartIcon className="w-8 h-8 text-gray-500 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-white mb-2">
            No pipeline data in this window
          </h3>
          {isMarketing ? (
            <p className="text-xs text-gray-400 max-w-md mx-auto">
              The pipeline surfaces deals from{' '}
              <code className="font-mono text-gray-300">crm_deal_snapshots</code>.
              Ask an admin to connect your CRM to start syncing deals.
            </p>
          ) : (
            <p className="text-xs text-gray-400 max-w-md mx-auto">
              The pipeline surfaces deals from{' '}
              <code className="font-mono text-gray-300">crm_deal_snapshots</code>.
              Connect MyCRM via{' '}
              <a
                href="/leads/setup"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Setup Wizard Step 6
              </a>{' '}
              or <a
                href="/settings/crm/connections"
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Integrations
              </a>{' '}
              to start syncing deals.
            </p>
          )}
        </div>
      )}

      {/* Funnel */}
      {hasData && data && data.funnel.length > 0 && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-700">
            <h3 className="text-sm font-semibold text-white">
              Pipeline funnel
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Deal count by current status — latest snapshot per deal
            </p>
          </div>
          <div className="p-5 space-y-3">
            {data.funnel.map((f, idx) => {
              const color = FUNNEL_COLORS[idx % FUNNEL_COLORS.length];
              const widthPct =
                funnelMax > 0 ? Math.max(4, (f.dealCount / funnelMax) * 100) : 4;
              return (
                <div key={f.dealStatus} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-300 font-medium">
                      {fmtStatus(f.dealStatus)}
                    </span>
                    <span className="text-gray-400 font-mono">
                      {f.dealCount.toLocaleString()} deals ·{' '}
                      {fmtDollars(f.totalLoanAmountCents)}
                    </span>
                  </div>
                  <div className="h-3 rounded-full bg-gray-900 overflow-hidden">
                    <div
                      className="h-full transition-all duration-500"
                      style={{ width: `${widthPct}%`, background: color }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stale deals */}
      {hasData && data && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-700 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h3 className="text-sm font-semibold text-white">
              Stale deals — needs follow-up
            </h3>
          </div>
          {data.staleDeals.length === 0 ? (
            <div className="px-5 py-8 text-center text-xs text-gray-500">
              No stale deals. Every deal in the pipeline has been updated in
              the last 30 days.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-900/50 text-gray-400 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">
                      Borrower
                    </th>
                    <th className="px-4 py-3 text-left font-medium">Adviser</th>
                    <th className="px-4 py-3 text-left font-medium">Lender</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-right font-medium">
                      Loan amount
                    </th>
                    <th className="px-4 py-3 text-right font-medium">
                      Days since update
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {data.staleDeals.slice(0, 50).map((d) => (
                    <tr key={d.dealId} className="text-gray-200">
                      <td className="px-4 py-3">
                        <div className="font-medium text-white">
                          {d.primaryBorrowerName ?? d.dealName ?? d.dealId}
                        </div>
                        {d.dealName && d.primaryBorrowerName && (
                          <div className="text-[10px] text-gray-500 mt-0.5">
                            {d.dealName}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-300">
                        {d.adviserName ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-400">
                        {d.lenderName ?? '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          {fmtStatus(d.dealStatus)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-mono">
                        {fmtDollars(d.loanAmountCents)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-amber-300">
                        {d.daysSinceLastUpdate}d
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.staleDeals.length > 50 && (
                <div className="px-4 py-3 text-[11px] text-gray-500 border-t border-gray-700">
                  Showing 50 of {data.staleDeals.length} stale deals. Filter
                  by adviser / lender in Wave 4.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PipelinePage;
